import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, RotateCcw, Waves, Wallet, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Stat, Tag, ConfirmButton } from '../ui'
import { get, post, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num, shortAddr } from '../../lib/format'

export default function ChainPage() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('networks')

  const load = () => {
    setLoading(true)
    get('/admin/chain/overview').then(setData).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
    const id = setInterval(load, 30000)
    return () => clearInterval(id)
  }, [])

  return (
    <>
      <PageHead title="شبکه‌های بلاک‌چین" subtitle="سلامت ناظر واریز، موجودی کیف پول گرم و ابزارهای عملیاتی"
        action={<RefreshButton onClick={load} loading={loading} />} />

      <div className="mb-5 flex flex-wrap gap-2">
        <Tag kind={data?.mainnet ? 'down' : 'gold'}>{data?.mainnet ? 'شبکه اصلی (Mainnet)' : 'شبکه آزمایشی (Testnet)'}</Tag>
        <Tag kind={data?.autoWithdraw ? 'up' : 'muted'}>برداشت {data?.autoWithdraw ? 'خودکار' : 'دستی'}</Tag>
        <Tag kind={data?.sweepEnabled ? 'up' : 'muted'}>جمع‌آوری {data?.sweepEnabled ? 'فعال' : 'غیرفعال'}</Tag>
        <Tag kind="info">اثر انگشت کیف پول: {data?.walletFingerprint ?? '—'}</Tag>
        <Tag kind="muted">چرخه اسکن هر {fa(Math.round(data?.watchInterval ?? 0))} ثانیه</Tag>
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="آدرس‌های واریز ساخته‌شده" value={fa(data?.depositAddresses ?? 0)} icon={<Wallet className="h-5 w-5" />} />
        <Stat label="واریز در انتظار تأیید" value={fa(data?.pendingDeposits ?? 0)} color="#FFB020" />
        <Stat label="برداشت در جریان" value={fa(data?.pendingWithdrawals ?? 0)} color="#22D3EE" />
        <Stat label="واریز جمع‌آوری‌نشده" value={fa(data?.unsweptDeposits ?? 0)} color="#00E39A" />
      </div>

      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {[['networks', 'شبکه‌ها و کیف پول گرم'], ['addresses', 'آدرس‌های واریز کاربران']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>{l}</button>
        ))}
      </div>

      {tab === 'networks' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {(data?.networks ?? []).map((n: any) => (
            <NetworkCard key={n.network} n={n} onDone={load} />
          ))}
          {!(data?.networks ?? []).length && (
            <Panel>
              <div className="flex items-center gap-2 py-8 text-xs text-slate-500">
                <AlertTriangle className="h-4 w-4" />
                شبکه فعالی وجود ندارد. تنظیمات RPC و کیف پول HD را بررسی کنید.
              </div>
            </Panel>
          )}
        </div>
      ) : <Addresses />}
    </>
  )
}

function NetworkCard({ n, onDone }: { n: any; onDone: () => void }) {
  const [sweeping, setSweeping] = useState('')
  const [report, setReport] = useState<any>(null)

  const sweep = async (symbol: string) => {
    setSweeping(symbol)
    setReport(null)
    try {
      const r: any = await post(`/admin/chain/sweep/${n.network}/${symbol}`)
      setReport({ ...r, symbol })
      const swept = r.swept?.length ?? 0
      if (swept) toast.ok(`${fa(swept)} آدرس جمع‌آوری شد`)
      else if (r.gasWarning) toast.err('جمع‌آوری انجام نشد — کیف پول گرم گس ندارد')
      else if (r.errors?.length) toast.err(r.errors[0])
      else toast.ok('چیزی برای جمع‌آوری نبود')
      onDone()
    } catch (e: any) { toast.err(e.message) } finally { setSweeping('') }
  }

  return (
    <Panel title={
      <div className="flex flex-wrap items-center gap-2">
        {n.healthy === false ? <AlertTriangle className="h-4 w-4 text-down" /> : <CheckCircle2 className="h-4 w-4 text-up" />}
        <span className="font-bold text-white">{n.faName}</span>
        <Tag kind="muted">{n.network}</Tag>
        <Tag kind="info">{fa(n.confirmations)} تأیید</Tag>
      </div>
    } action={
      <ConfirmButton variant="ghost" label="بازخوانی بلاک‌ها" question="۵۰۰ بلاک عقب برود؟"
        icon={<RotateCcw className="h-3.5 w-3.5" />}
        onConfirm={async () => {
          try { await post(`/admin/chain/cursor/${n.network}/resync?blocks=500`); toast.ok('نشانگر اسکن عقب رفت'); onDone() }
          catch (e: any) { toast.err(e.message) }
        }} />
    }>
      <div className="mb-3 grid grid-cols-3 gap-2 text-center">
        {[['آخرین بلاک اسکن‌شده', fa(n.lastBlock)], ['آخرین بلاک شبکه', fa(n.tip)],
          ['فاصله', n.lag === null ? '—' : fa(n.lag)]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-white/[.06] bg-white/[.02] p-2.5">
            <div className="mb-1 text-[10px] text-slate-500">{k}</div>
            <div className="num text-sm text-slate-200">{v}</div>
          </div>
        ))}
      </div>

      <div className="mb-3 rounded-xl border border-white/[.06] bg-white/[.02] p-3">
        <div className="mb-1 flex items-center justify-between text-[10px] text-slate-500">
          <span className="flex items-center gap-1.5"><Wallet className="h-3 w-3" /> کیف پول گرم</span>
          <a href={n.explorer} target="_blank" rel="noreferrer" className="text-brand-300 hover:underline">
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="num break-all text-[11px] text-slate-300" dir="ltr">{n.hotAddress}</div>
      </div>

      <div className="space-y-1.5">
        {n.balances.map((b: any) => (
          <div key={b.symbol} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-white/[.03]">
            <span className="text-slate-300">{b.symbol}</span>
            {b.kind === 'token' && <Tag kind="muted">توکن</Tag>}
            <span className={clsx('num mr-auto', b.balance === null ? 'text-slate-600' : 'text-slate-200')}>
              {b.balance === null ? 'در دسترس نیست' : num(b.balance, 8)}
            </span>
            <button onClick={() => sweep(b.symbol)} disabled={sweeping === b.symbol}
              className="btn-ghost !px-2 !py-1 text-[10px]">
              <Waves className="h-3 w-3" /> {sweeping === b.symbol ? '…' : 'جمع‌آوری'}
            </button>
          </div>
        ))}
      </div>

      {/* گزارش آخرین جمع‌آوری — چرا انجام شد یا نشد */}
      {report && (
        <div className="mt-3 space-y-2">
          {report.gasWarning && (
            <div className="rounded-lg border border-gold/30 bg-gold/[.08] p-2.5 text-[11px] leading-6 text-slate-200">
              <b className="text-gold">کمبود گس:</b> {report.gasWarning}
              <div className="mt-1 text-slate-400">
                توکن خودش کارمزد شبکه را نمی‌پردازد؛ برای جابه‌جا کردن آن، آدرس واریز
                باید {report.nativeSymbol} داشته باشد و آن را کیف پول گرم می‌فرستد.
              </div>
            </div>
          )}
          {report.swept?.length > 0 && (
            <div className="rounded-lg border border-up/25 bg-up/[.07] p-2 text-[11px] leading-6 text-up">
              {fa(report.swept.length)} آدرس جمع‌آوری شد ({report.symbol})
            </div>
          )}
          {report.notes?.map((x: any, i: number) => (
            <div key={i} className="rounded-lg bg-white/[.04] p-2 text-[11px] leading-5 text-slate-400">
              <span className="num" dir="ltr">{String(x.address).slice(0, 14)}…</span> — {x.reason}
            </div>
          ))}
          {report.errors?.map((e: string, i: number) => (
            <div key={i} className="rounded-lg bg-down/10 p-2 text-[11px] leading-5 text-down">{e}</div>
          ))}
          {!report.swept?.length && !report.errors?.length && !report.gasWarning && (
            <div className="rounded-lg bg-white/[.04] p-2 text-[11px] text-slate-400">
              {fa(report.scanned ?? 0)} آدرس بررسی شد؛ موجودی قابل جمع‌آوری نداشتند.
            </div>
          )}
        </div>
      )}

      {n.error && <div className="mt-3 rounded-lg bg-down/10 p-2 text-[11px] leading-5 text-down">{n.error}</div>}
    </Panel>
  )
}

function Addresses() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [f, setF] = useState({ family: '', q: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/chain/addresses${qs({ ...f, per_page: 40 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })} placeholder="جستجوی آدرس…" />
          <Select value={f.family} onChange={(family) => setF({ ...f, family, page: 1 })}
            options={[['', 'همه خانواده‌ها'], ['evm', 'EVM'], ['tron', 'TRON'], ['btc', 'Bitcoin'], ['sol', 'Solana']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={720} empty="آدرسی ساخته نشده است" columns={[
        { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r: any) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'f', header: 'خانواده', cell: (r: any) => <Tag kind="muted">{r.family.toUpperCase()}</Tag> },
        { key: 'a', header: 'آدرس', cell: (r: any) => <span className="num text-slate-300" dir="ltr">{r.address}</span> },
        { key: 'i', header: 'اندیس', cell: (r: any) => <span className="num text-slate-500">{fa(r.index)}</span> },
        { key: 'd', header: 'ساخت', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span> },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}
